import { useState } from "react";
import { Link } from "react-router-dom";
import { PRO_PRICING, PRO_ANNUAL_MONTHLY_EFFECTIVE, PRO_FEATURES } from "@garageregister/shared";
import {
  Check,
  Crown,
  Wrench,
  Warehouse,
  Car,
  Sparkles,
  Eye,
  MapPin,
  Scan,
  History,
} from "lucide-react";

type BillingInterval = "monthly" | "annual";

const proMonthly = PRO_PRICING.monthlyPrice;
const proAnnual = PRO_PRICING.annualPrice;
const savingsPercent = PRO_PRICING.annualSavingsPercent;

export function PricingSection() {
  const [interval, setInterval] = useState<BillingInterval>("annual");

  const displayPrice = interval === "annual" ? PRO_ANNUAL_MONTHLY_EFFECTIVE : proMonthly;
  const displayPeriod = interval === "annual" ? "/mo" : "/month";

  return (
    <section id="pricing" className="bg-[#0C1222] py-20 sm:py-28">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="font-heading text-3xl font-bold text-[#E8EDF2] sm:text-4xl">
            Simple, transparent pricing
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-[#7A8FA3]">
            Core vehicle tracking is free forever. Upgrade for AI scanning,
            performance logs, photos, and the full suite.
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full bg-[#151D2E] p-1">
            <button
              onClick={() => setInterval("monthly")}
              className={`rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
                interval === "monthly"
                  ? "bg-[#1E2B3D] text-[#E8EDF2] shadow-sm"
                  : "text-[#7A8FA3] hover:text-[#E8EDF2]"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("annual")}
              className={`flex items-center gap-2 rounded-full px-5 py-1.5 text-sm font-medium transition-colors ${
                interval === "annual"
                  ? "bg-[#1E2B3D] text-[#E8EDF2] shadow-sm"
                  : "text-[#7A8FA3] hover:text-[#E8EDF2]"
              }`}
            >
              Annual
              <span className="rounded-full bg-[#4DB8C7]/20 px-2 py-0.5 text-[10px] font-bold text-[#4DB8C7]">
                Save {savingsPercent}%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-12 grid gap-8 md:grid-cols-2">
          <div className="rounded-xl border border-[#253448] bg-[#151D2E] p-8">
            <h3 className="font-heading text-xl font-semibold text-[#E8EDF2]">Free</h3>
            <p className="mt-4">
              <span className="text-4xl font-bold text-[#E8EDF2]">$0</span>
              <span className="ml-1 text-sm text-[#7A8FA3]">/forever</span>
            </p>
            <p className="mt-4 text-sm text-[#7A8FA3]">
              Perfect for getting started with basic vehicle tracking.
            </p>

            <a
              href="/get"
              className="mt-8 block rounded-lg border border-[#253448] py-3 text-center text-sm font-semibold text-[#E8EDF2] transition-colors hover:border-[#4DB8C7]/50 hover:bg-[#1E2B3D]"
            >
              Download free
            </a>

            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <Warehouse className="h-4 w-4 text-[#4DB8C7]" />
                1 garage
              </li>
              <li className="flex items-start gap-2 text-[#E8EDF2]">
                <Car className="mt-0.5 h-4 w-4 shrink-0 text-[#4DB8C7]" />
                <span>
                  Up to 3 vehicles
                  <span className="block text-xs text-[#7A8FA3]">
                    Past rides don&apos;t count toward this limit
                  </span>
                </span>
              </li>
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <Wrench className="h-4 w-4 text-[#4DB8C7]" />
                Unlimited service & mod logs
              </li>
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <Scan className="h-4 w-4 text-[#4DB8C7]" />
                5 lifetime AI document scans
              </li>
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <Eye className="h-4 w-4 text-[#4DB8C7]" />
                Spotted posts &mdash; free for everyone
              </li>
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <MapPin className="h-4 w-4 text-[#4DB8C7]" />
                Meets &mdash; RSVP and spot
              </li>
              <li className="flex items-center gap-2 text-[#E8EDF2]">
                <History className="h-4 w-4 text-[#4DB8C7]" />
                Past rides
              </li>
            </ul>
          </div>

          <div className="relative rounded-xl border border-[#4DB8C7]/30 bg-[#4DB8C7]/5 p-8">
            <div className="absolute -top-3 right-6">
              <span className="rounded-full bg-[#4DB8C7] px-3 py-1 text-xs font-bold text-[#0C1222]">
                Recommended
              </span>
            </div>

            <h3 className="flex items-center gap-2 font-heading text-xl font-semibold text-[#E8EDF2]">
              <Crown className="h-5 w-5 text-[#4DB8C7]" />
              Pro
            </h3>
            <p className="mt-4">
              <span className="text-4xl font-bold text-[#E8EDF2]">${displayPrice.toFixed(2)}</span>
              <span className="ml-1 text-sm text-[#7A8FA3]">{displayPeriod}</span>
            </p>
            {interval === "annual" && (
              <p className="mt-1 text-xs text-[#7A8FA3]">
                ${proAnnual}/year — billed annually ·{" "}
                <span className="text-[#4DB8C7]">first month free</span>
              </p>
            )}
            <p className="mt-4 text-sm text-[#7A8FA3]">
              Everything in Free, plus the full suite of tools.
            </p>

            <Link
              to="/signup"
              className="mt-8 block rounded-lg bg-[#4DB8C7] py-3 text-center text-sm font-semibold text-[#0C1222] shadow-[0_0_20px_rgba(77,184,199,0.25)] transition-all hover:bg-[#3DA8B7] hover:shadow-[0_0_30px_rgba(77,184,199,0.35)]"
            >
              {interval === "annual"
                ? `Get Pro — first month free`
                : `Upgrade to Pro — $${proMonthly}/month`}
            </Link>

            <ul className="mt-8 space-y-3 text-sm">
              {PRO_FEATURES.map((f) => (
                <li
                  key={f.label}
                  className={`flex items-start gap-2 text-[#E8EDF2] ${
                    f.highlight ? "rounded-md bg-[#4DB8C7]/5 px-2 py-1.5 -mx-2" : ""
                  }`}
                >
                  {f.highlight ? (
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#4DB8C7]" />
                  ) : (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#4DB8C7]" />
                  )}
                  <div>
                    <span className="font-medium">{f.label}</span>
                    <span className="text-[#7A8FA3]"> — {f.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
